/*
 * Figma `77:37` — Region / Section Navigation, the rail down the left of the Landing editor.
 *
 * A tab list, because that is what it is: picking an entry swaps the panel beside it. Roving tab
 * stop and arrow-key movement come with the pattern, and the editor panel gets `role="tabpanel"`
 * pointing back, so a screen reader announces "ข้อมูลหลัก, tab 1 of 6" rather than reading six
 * unexplained buttons.
 *
 * Each entry carries a completeness glyph and a status line. That is the part worth having: a long
 * form split into six panels hides five of them, and without a summary on the rail the only way to
 * find the section that still needs work is to open all six. The glyph is never the only signal —
 * the status line says the same thing in words.
 *
 * At tablet and phone widths the rail becomes a horizontal scroller (Figma `81:194`), which is CSS
 * on the same markup rather than a second component.
 */
import type { ReactNode } from 'react';

export type SectionStatus = 'complete' | 'attention' | 'empty';

export interface SectionNavItem {
  key: string;
  label: string;
  /** In words — "ครบแล้ว", "3 ศิลปิน", "ยังไม่ได้กรอก". Never left to the glyph alone. */
  status: string;
  tone?: SectionStatus;
}

const MARK: Record<SectionStatus, string> = { complete: '✓', attention: '!', empty: '·' };

export function SectionNav({ items, active, onSelect, label, panelId }: {
  items: readonly SectionNavItem[];
  active: string;
  onSelect: (key: string) => void;
  /** Names the rail, e.g. "เนื้อหาหน้าเว็บ". Shown, and used as the list's accessible name. */
  label: string;
  /** The id of the panel these tabs control. */
  panelId: string;
}) {
  const move = (delta: number) => {
    const at = items.findIndex((i) => i.key === active);
    const next = items[(at + delta + items.length) % items.length];
    if (next) onSelect(next.key);
  };

  return (
    <div className="jt-sectionnav">
      <p className="jt-sectionnav-label" id={`${panelId}-navlabel`}>{label}</p>
      <div
        className="jt-sectionnav-list"
        role="tablist"
        aria-labelledby={`${panelId}-navlabel`}
        aria-orientation="vertical"
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); move(1); }
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
        }}
      >
        {items.map((item) => {
          const on = item.key === active;
          const tone = item.tone ?? 'complete';
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              id={`${panelId}-tab-${item.key}`}
              className="jt-sectionnav-item"
              data-tone={tone}
              aria-selected={on}
              aria-controls={panelId}
              tabIndex={on ? 0 : -1}
              onClick={() => onSelect(item.key)}
            >
              <span className="jt-sectionnav-mark" aria-hidden="true">{MARK[tone]}</span>
              <span className="jt-sectionnav-copy">
                <span className="jt-sectionnav-title">{item.label}</span>
                <span className="jt-sectionnav-status">{item.status}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The panel the rail controls. Labelled by the active tab, so the two stay tied together. */
export function SectionPanel({ id, activeKey, children }: {
  id: string;
  activeKey: string;
  children: ReactNode;
}) {
  return (
    <div id={id} role="tabpanel" aria-labelledby={`${id}-tab-${activeKey}`} tabIndex={-1} className="jt-sectionpanel">
      {children}
    </div>
  );
}
